<template>
    <nav :class="disabled ? 'bg-primary' : 'bg-neutral-grey'"
         class="h-screen flex-none flex flex-col w-64 items-center fixed z-90">

        <div class="pt-16 mb-12 border-b border-white flex-none">
            <router-link :disabled="disabled" to="/">
                <button class="flex mb-16">
                    <svg-icon type="logo-white" size="40" />
                </button>
            </router-link>
        </div>

        <ul class="w-full flex flex-col items-center flex-grow">
            <template v-if="companyActive" v-for="(item, key) in navigationItems" :key="key">
                <li class="w-full"
                    :id="!item.navigationLink ? 'nav-' + key : null"
                    :class="!item.navigationLink ? 'flex h-48 w-full items-center justify-center ' + (key === 'help' ? 'order-last mt-auto mb-16' : 'relative mb-8') : null"
                >
                    <feature
                        v-if="((!disabled && item.feature && (item.can ? can[item.can] : true)) || (item.setActive !== null ? overrideMenuItems.hasOwnProperty(item.linkTo) : null)) && item.navigationLink"
                        v-slot="{ id }"
                        :id="'nav-' + key"
                        :feature-key="key"
                        sub-key="menu"
                        class="w-full">
                        <navigation-link
                            :icon="item.icon"
                            :link-to="item.linkTo"
                            :set-active="item.setActive ? overrideMenuItems[item.linkTo] : false"
                            :tooltip="capitalize(item.tooltip)"
                            :is-new="isNew(item.linkTo)"
                        />
                    </feature>
                    <navigation-link
                        v-else-if="((!disabled && (item.can !== null ? can[item.can] : true)) || (item.setActive !== null ? overrideMenuItems.hasOwnProperty(item.linkTo) : null)) && item.navigationLink"
                        :id="`nav-${item.linkTo}`"
                        :set-active="item.setActive ? overrideMenuItems[item.linkTo] : false"
                        :icon="item.icon"
                        :link-to="item.linkTo"
                        :tooltip="capitalize(item.tooltip)"
                        :is-new="isNew(item.linkTo)"
                    />
                    <tooltip
                        v-else-if="!item.navigationLink"
                        :content="capitalize(item.tooltip)"
                        offset="0,16"
                        display="light"
                    >
                        <a :class="'cursor-pointer trans-ease ' + (key !== 'help' ? 'text-white block hover:text-secondary' : 'w-40 h-40 border-2 border-white bg-primary-20 rounded-32 flex items-center justify-center text-neutral-grey')"
                           :href="item.linkTo"
                           target="_blank">
                            <svg-icon size="24" :type="item.icon" />
                        </a>
                    </tooltip>
                </li>
            </template>
        </ul>
    </nav>
</template>

<script>
import Tooltip from './Tooltip.vue';
import SvgIcon from '../ui/SvgIcon.vue';
import Feature from '../Feature.vue';
import NavigationLink from './NavigationLink';
import capitalize from "../../mixins/capitalize";
import {useMainStore} from "../../state/store";
import {mapStores} from "pinia";
import FeatureModel from "../../models/Feature";
import {useRepo} from "pinia-orm";

export default {
    components: {
        NavigationLink,
        Feature,
        'tooltip': Tooltip,
        'svg-icon': SvgIcon,
    },
    mixins: [capitalize],
    props: {
        disabled: Boolean,
    },
    data() {
        return {
            navigationItems: {
                dashboard: {
                    icon: 'grid-view',
                    linkTo: 'dashboard',
                    tooltip: 'Dashboard',
                    setActive: false,
                    can: false,
                    feature: true,
                    navigationLink: true,
                },
                jobs: {
                    icon: 'format-list-bulleted',
                    linkTo: 'jobs',
                    tooltip: this.$t('jobs.page_title'),
                    setActive: true,
                    can: 'jobs',
                    feature: false,
                    navigationLink: true,
                },
                applications: {
                    icon: 'applications',
                    linkTo: 'applications',
                    tooltip: this.$t('applications.title'),
                    setActive: true,
                    can: 'applications',
                    feature: false,
                    navigationLink: true,
                },
                interviews: {
                    icon: 'calendar-today',
                    linkTo: 'interviews',
                    tooltip: this.$t('interview.interviews'),
                    setActive: true,
                    can: 'applications',
                    feature: false,
                    navigationLink: true,
                },
                multiposting: {
                    icon: 'campaigns',
                    linkTo: 'multi-posting',
                    tooltip: this.$t('multi-posting.text-icon'),
                    setActive: true,
                    can: 'campaigns',
                    feature: false,
                    navigationLink: true,
                },
                jobsites: {
                    icon: 'format-paint',
                    linkTo: 'branding',
                    tooltip: this.$t('site_editor.title'),
                    setActive: true,
                    can: 'jobsite',
                    feature: false,
                    navigationLink: true,
                },
                interim: {
                    icon: 'interim',
                    linkTo: 'interims',
                    tooltip: this.$t('interims.title'),
                    setActive: true,
                    can: 'interim',
                    feature: true,
                    navigationLink: true,
                },
                gdpr: {
                    icon: 'gdpr',
                    linkTo: 'data-protection',
                    tooltip: this.$t('data_protection.title'),
                    setActive: false,
                    can: 'applications',
                    feature: false,
                    navigationLink: true,
                },
                stats: {
                    icon: 'bar-chart',
                    linkTo: 'analytics',
                    tooltip: this.$t('analytics.title'),
                    setActive: false,
                    can: 'analytics',
                    feature: false,
                    navigationLink: true,
                },
                settings: {
                    icon: 'settings',
                    linkTo: 'settings.company',
                    tooltip: this.$t('settings.title'),
                    setActive: false,
                    can: 'settings',
                    feature: false,
                    navigationLink: true,
                },
                marketplace: {
                    icon: 'shopping-bag',
                    linkTo: 'https://marketplace.jobtoolz.be',
                    tooltip: this.$t('general.marketplace'),
                    setActive: false,
                    can: false,
                    feature: false,
                    navigationLink: false,
                },
                help: {
                    icon: 'question-mark',
                    linkTo: 'https://help.jobtoolz.com',
                    tooltip: this.$t('header.help'),
                    setActive: false,
                    can: false,
                    feature: false,
                    navigationLink: false,
                },
            }
        }
    },
    computed: {
        ...mapStores(useMainStore),
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        companyActive() {
            return this.currentCompany && this.currentCompany.active;
        },
        gdprEnabled() {
            return this.currentCompany && this.currentCompany.data_retention_period !== null;
        },
        settingsHome() {
            if (!this.companyActive) {
                return 'settings.pricing-plans';
            }

            return 'settings.company';
        },
        can() {
            return {
                applications: this.$can([
                    'applications.view',
                    'applications.view.*',
                    'job-applications.view.*',
                ]),
                jobs: this.$can([
                    'jobs.view',
                    'jobs.view.*',
                    'jobs.create',
                    'jobs.manage',
                ]),
                campaigns: this.$can([
                    'campaigns.create',
                    'campaigns.update',
                    'campaigns.delete',
                    'campaigns.view',
                ]),
                interim: this.$can([
                    'interims.create',
                    'interims.update',
                    'interims.delete',
                    'interims.view',
                ]),
                jobsite: this.$can(['jobsites.view', 'jobsites.manage']),
                analytics: this.$can(['analytics.view']),
                settings: this.$can([
                    'settings.general',
                    'settings.finance',
                    'settings.applications',
                    'settings.jobsite',
                    'settings.advanced',
                ]),
            };
        },
        helpCenterUrl() {
            let lang = 'nl';

            // > Disabled until we have help pages in other languages <
            //if (Jobtoolz.user && Jobtoolz.user.attributes.language)
            //    lang = Jobtoolz.user.attributes.language;

            return `https://help.jobtoolz.com/${lang}`;
        },
        overrideMenuItems() {
            return this.mainStore.overrideMenuItems !== null ? this.mainStore.overrideMenuItems : [];
        },
    },
    methods: {
        isNew(feature) {
            feature = useRepo(FeatureModel).where('key', feature + '_new').first();
            return feature && this.currentCompany.feature_ids.includes(feature.id);
        }
    }
};
</script>
